import { useCallback } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CircleAlert,
  EyeOff,
  FolderInput,
  RefreshCw,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { usePermission } from '@/Hooks/usePermission';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { buttonVariants } from '@/components/ui/button-variants';
import { Card, CardContent } from '@/components/ui/card';
import { Icon } from '@/components/ui/icon';
import { LoadingState } from '@/components/ui/states';
import { Tabs, TabPanel } from '@/components/ui/tabs';
import { useSpacesChain } from '@/Pages/Settings/components/spaces/useSpacesChain';
import { StepBody } from './steps/StepBody';
import { useSetupStatus, type SetupStep, type StepId, type StepState } from './useSetupStatus';

const TONE: Record<StepState, 'success' | 'warning' | 'neutral' | 'danger'> = {
  done: 'success',
  missing: 'warning',
  restricted: 'neutral',
  error: 'danger',
};

/**
 * C-15 — the guided setup, as a wizard.
 *
 * ## What this is, and what it therefore cannot have
 *
 * It is a guided **view over live data**, not a form. Every step writes to the
 * API the moment the operator saves in it — a floor created on step two exists
 * on step two, whether or not step nine is ever reached. That was the request,
 * in the venue owner's words: *"every step adds directly — not until finish"*.
 *
 * Three consequences follow, and they are the reason this comment is long:
 *
 *  1. **There is no Cancel and there can never be one.** Nothing is held back to
 *     be committed, so there is nothing to discard. A Cancel button here would
 *     either do nothing or lie about undoing nine writes across five endpoints,
 *     none of which the API offers a rollback for. If you are about to add one:
 *     the honest control is Back, or leaving the screen, and both already work.
 *  2. **Leaving halfway is safe, and resumable.** State comes from the probes in
 *     `useSetupStatus`, not from anything remembered here, so reopening `/setup`
 *     lands on the first step the API still says is unfinished — from any
 *     device, after any other person's edits.
 *  3. **A step is never consumed.** `done` means "you may move on", not "this is
 *     closed": every step stays open for a second area, a rename or a delete,
 *     and the strip lets the operator go straight back to any of them.
 *
 * ## Why the tick is never local
 *
 * A step's state is read back from the API after every write and from nowhere
 * else. `onWrote` refreshes the probes; it does not mark anything done. Inferring
 * done-ness from "the create dialog just closed" is how a write the server
 * refused shows up as a green tick — the one failure this screen must not have,
 * because the whole point of it is telling an operator the truth about what
 * their venue is still missing.
 *
 * ## The strip
 *
 * A real `tablist` (`components/ui/tabs`): arrow keys move along it, Home and
 * End jump to the ends, only the selected step is in the Tab order, and the
 * arrows follow the writing direction rather than always meaning "right". The
 * step is a route segment for the same reason every other tab in this app is
 * one — a bookmark, a link in a message and the back button all have to work.
 */
export default function SetupWizard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can } = usePermission();
  const { step: segment } = useParams<{ step: string }>();
  const { steps, loading, complete, reload } = useSetupStatus();

  /**
   * The spaces hierarchy, owned here rather than by the floor, area and table
   * steps: those three are three views of ONE chain, and a step component
   * unmounts on every Next. `autoSelect` because a wizard that leaves the parent
   * unchosen can only offer a disabled Add button.
   */
  const chain = useSpacesChain(true);

  const go = useCallback((id: StepId) => navigate(`/setup/${id}`), [navigate]);

  if (!steps.length) return <LoadingState label={t('common.loading')} />;

  const index = steps.findIndex((step) => step.id === segment);
  // The bare `/setup`, a renamed step in an old link, or a typo. Not a refusal —
  // every step is visible to everyone who can reach this screen at all — so it
  // lands on the first unfinished one rather than on a 403.
  if (index < 0) return <Navigate to={`/setup/${landingStep(steps)}`} replace />;

  const current = steps[index];
  const done = steps.filter((step) => step.state === 'done').length;
  const known = steps.filter((step) => step.state !== 'restricted' && step.state !== 'error').length;

  const last = index === steps.length - 1;
  const nextLabel = last
    ? t('setup.wizard.finish')
    : current.state === 'missing'
      ? t('setup.wizard.skip')
      : t('common.next');

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t('setup.wizard.title')}</h1>
          <p className="max-w-2xl text-sm text-ink-muted">{t('setup.wizard.subtitle')}</p>
        </div>

        <div className="flex items-center gap-3">
          <Badge tone={complete ? 'success' : 'warning'}>
            {t('setup.checklist.progress', { done, total: known })}
          </Badge>
          <Button variant="outline" size="sm" onClick={reload} disabled={loading}>
            <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
            {t('setup.checklist.refresh')}
          </Button>
        </div>
      </div>

      {/* ADR-0007 — the Migration Center. Not a step here: it is a job with a
          mapping screen and a history of its own, not a presence check
          `useSetupStatus` can probe, and this wizard's own promise is that
          leaving it changes nothing about it (§ note above) — folding in a
          multi-page import flow would break that. A plain link instead, in
          the place a new venue already is when the "type it all in by hand or
          bring a spreadsheet" question comes up. Gated on `imports.view` —
          the fix keys above get someone onto this screen; whether they can
          also see the importer is its own, separate permission. */}
      {can('imports.view') && (
        <Card>
          <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
            <div className="flex min-w-0 items-start gap-3">
              <span className="mt-0.5 shrink-0 text-ink-muted">
                <Icon as={FolderInput} aria-hidden />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-medium">{t('setup.wizard.migrationCenter.title')}</p>
                <p className="text-sm text-ink-muted">{t('setup.wizard.migrationCenter.hint')}</p>
              </div>
            </div>
            <Link to="/imports" className={buttonVariants({ variant: 'outline', size: 'sm' })}>
              {t('setup.wizard.migrationCenter.cta')}
              <Icon as={ArrowRight} aria-hidden />
            </Link>
          </CardContent>
        </Card>
      )}

      {complete && (
        <Card>
          <CardContent className="flex items-start gap-3 p-4">
            <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-green-100 text-green-700">
              <Icon as={Check} size="sm" aria-hidden />
            </span>
            <div>
              <p className="text-sm font-medium">{t('setup.checklist.ready.title')}</p>
              <p className="text-sm text-ink-muted">{t('setup.checklist.ready.hint')}</p>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs
        tabs={steps.map((step) => ({
          key: step.id,
          label: t(`setup.wizard.short.${step.id}`),
          badge: <StepMark step={step} />,
        }))}
        value={current.id}
        onChange={go}
        label={t('setup.wizard.strip')}
        idPrefix="setup"
        className="scrollbar-thin gap-x-5 overflow-x-auto"
      />

      <TabPanel idPrefix="setup" tab={current.id} className="space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold">
              {t(`setup.checklist.steps.${current.id}.title`)}
            </h2>
            <p className="max-w-2xl text-sm text-ink-muted">
              {t(`setup.checklist.steps.${current.id}.why`)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge tone={TONE[current.state]}>{t(`setup.checklist.state.${current.state}`)}</Badge>
            {current.state === 'done' && current.count > 0 && (
              <span className="text-xs text-ink-muted tabular-nums">
                {t('setup.checklist.found', { n: current.count })}
              </span>
            )}
          </div>
        </div>

        {/* Done is "you may move on", never "this is closed" — the panel below
            stays live so a second area, a rename or a delete needs no detour. */}
        {current.state === 'done' && (
          <p className="text-sm text-ink-muted">{t('setup.wizard.doneHint')}</p>
        )}

        <StepBody
          step={current}
          chain={chain}
          // The venue changed, so go and ask the API what it says now. This is
          // the only thing that can move a step's state, and it deliberately
          // does not know whether the write it followed succeeded.
          onWrote={reload}
          onReload={reload}
          onBack={() => index > 0 && go(steps[index - 1].id)}
        />
      </TabPanel>

      {/* No Cancel: see the note at the top of this file. Back and Next move
          through the steps; neither commits nor discards anything, because
          everything has already been written. */}
      <div className="flex flex-wrap items-center gap-3 border-t pt-4">
        <span className="text-sm text-ink-muted">
          {t('common.stepOfTotal', { step: index + 1, total: steps.length })}
        </span>
        {current.state === 'missing' && !last && (
          <span className="text-xs text-ink-muted">{t('setup.wizard.skipHint')}</span>
        )}
        <div className="ms-auto flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            disabled={index === 0}
            onClick={() => go(steps[index - 1].id)}
          >
            <Icon as={ArrowLeft} aria-hidden />
            {t('common.back')}
          </Button>
          <Button
            size="sm"
            onClick={() => (last ? navigate('/') : go(steps[index + 1].id))}
          >
            {nextLabel}
            {!last && <Icon as={ArrowRight} aria-hidden />}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Where `/setup` opens.
 *
 * The first step the API still calls missing, so an operator who stopped after
 * the tables resumes at the stations rather than at the branch they made a week
 * ago. A `restricted` step is skipped over, because "unknown" is not "unfinished"
 * and nobody can act on it from here anyway; an `error` step is only landed on
 * when nothing is actually missing, since a failed probe is worth seeing but is
 * not work. With nothing left, the last step — which is where the finished
 * summary is.
 */
function landingStep(steps: SetupStep[]): StepId {
  return (
    steps.find((step) => step.state === 'missing')?.id ??
    steps.find((step) => step.state === 'error')?.id ??
    steps[steps.length - 1].id
  );
}

/**
 * The state of a step, in the strip.
 *
 * Four different glyphs rather than four colours: colour alone is not an
 * indicator anyone can rely on, and the strip is the one place the state is not
 * also written out in words. The label is what a screen reader appends to the
 * step's name — "Area, To do".
 */
function StepMark({ step }: { step: SetupStep }) {
  const { t } = useTranslation();
  const label = t(`setup.checklist.state.${step.state}`);

  if (step.state === 'done') {
    return (
      <span role="img" aria-label={label} className="text-green-700 dark:text-green-400">
        <Icon as={Check} size="sm" aria-hidden className="inline" />
      </span>
    );
  }
  if (step.state === 'restricted') {
    return (
      <span role="img" aria-label={label} className="text-ink-muted">
        <Icon as={EyeOff} size="sm" aria-hidden className="inline" />
      </span>
    );
  }
  if (step.state === 'error') {
    return (
      <span role="img" aria-label={label} className="text-red-600 dark:text-red-400">
        <Icon as={CircleAlert} size="sm" aria-hidden className="inline" />
      </span>
    );
  }
  return (
    <span
      role="img"
      aria-label={label}
      className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500 align-middle"
    />
  );
}
